import { useApp } from 'contexts/app';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/StudentViewSubmission/PageProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, IconButton, Stack, styled, Text } from 'shell/components';
import { FEEDBACK_LIMIT } from 'utils/constants';

const Header = () => {
    const { currentAssignment, studentSubmissions } = useApp();
    const { t } = useTranslation('assignment');
    const { device } = useViewport();

    const {
        isGenerating,
        currentSubmissionIndex,
        onClickBack,
        onClickInfo,
        onClickPrevAttempt,
        onClickNextAttempt,
        onClickRegenerate,
    } = useContext(PageContext);

    const canClickPrev = !isGenerating && currentSubmissionIndex > 0;
    const canClickNext = !isGenerating && currentSubmissionIndex < studentSubmissions.length - 1;

    return device === 'mobile' ? (
        <RootStack device={device} spacing={4}>
            <Stack alignItems={'center'} direction={'row'} spacing={4}>
                <Button
                    disabled={isGenerating}
                    startIcon={<Icon kind={'arrowLeft'} />}
                    variant={'borderless'}
                    onClick={onClickBack}
                >
                    {t('common:controls.back', 'Back')}
                </Button>
                <Text
                    sx={{ flex: 1, minWidth: 0 }}
                    component={'h1'}
                    noWrap
                    tooltip={{ component: currentAssignment.title }}
                    variant={'p1'}
                    weight={'medium'}
                >
                    {currentAssignment.title}
                </Text>
            </Stack>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <IconButton
                        aria-label={'previous draft'}
                        disabled={!canClickPrev}
                        kind={'chevronLeft'}
                        size={'small'}
                        tooltip={{ component: 'Previous Draft' }}
                        variant={'square'}
                        onClick={onClickPrevAttempt}
                    />
                    <Text>
                        Draft {currentSubmissionIndex + 1}/{FEEDBACK_LIMIT}
                    </Text>
                    <IconButton
                        aria-label={'next draft'}
                        disabled={!canClickNext}
                        kind={'chevronRight'}
                        size={'small'}
                        tooltip={{ component: 'Next Draft' }}
                        variant={'square'}
                        onClick={onClickNextAttempt}
                    />
                </Stack>
                <IconButton
                    aria-label={'regenerate'}
                    disabled={isGenerating}
                    kind={'refresh'}
                    size={'small'}
                    tooltip={{ component: t('common:controls.regenerate', 'Regenerate') }}
                    variant={'square'}
                    onClick={onClickRegenerate}
                />
            </Stack>
        </RootStack>
    ) : (
        <RootStack alignItems={'center'} device={device} direction={'row'} spacing={4}>
            <Button
                disabled={isGenerating}
                startIcon={<Icon kind={'arrowLeft'} />}
                variant={'borderless'}
                onClick={onClickBack}
            >
                {t('common:controls.back', 'Back')}
            </Button>
            <Text sx={{ flex: 1, minWidth: 0 }} component={'h1'} noWrap variant={'p1'} weight={'medium'}>
                {currentAssignment.title}
            </Text>
            <Button variant={'borderless'} onClick={onClickInfo}>
                {t('feedback-section.feedback-introduction.how-does-it-work', 'How does it work?')}
            </Button>
            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                <IconButton
                    aria-label={'previous draft'}
                    disabled={!canClickPrev}
                    kind={'chevronLeft'}
                    size={'small'}
                    tooltip={{ component: 'Previous Draft' }}
                    variant={'square'}
                    onClick={onClickPrevAttempt}
                />
                <Text>
                    Draft {currentSubmissionIndex + 1}/{FEEDBACK_LIMIT}
                </Text>
                <IconButton
                    aria-label={'next draft'}
                    disabled={!canClickNext}
                    kind={'chevronRight'}
                    size={'small'}
                    tooltip={{ component: 'Next Draft' }}
                    variant={'square'}
                    onClick={onClickNextAttempt}
                />
            </Stack>
            <IconButton
                aria-label={'regenerate'}
                disabled={isGenerating}
                kind={'refresh'}
                size={'small'}
                tooltip={{ component: t('common:controls.regenerate', 'Regenerate') }}
                variant={'square'}
                onClick={onClickRegenerate}
            />
        </RootStack>
    );
};

const RootStack = styled(Stack)(({ theme, device }) => {
    return {
        position: 'absolute',
        top: 0,
        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        width: '100%',
        height: device === 'mobile' ? '89px' : '49px',
        padding: theme.spacing(3, 5),
        background: theme.palette.ensightful.shades[0],
    };
});

export default Header;
